<script>
  import { onMount, onDestroy } from 'svelte';
  import Button from '../components/Button.svelte';
  import PricingCard from '../components/PricingCard.svelte';
  import { pricingSection } from './participate.js';
  import { resolveProductId } from './registration.js';
  import { precalculateOrder } from '../services/gigmaApi.js';

  const targetMs = new Date(pricingSection.countdown.targetDate).getTime();
  let now = Date.now();
  let intervalId;

  // Динамические цены тарифов из Gigma. Пока не загрузились — карточки
  // показывают хардкод-значения из participate.js. При успехе precalculate
  // подменяем на актуальную сумму. Если упало — оставляем хардкод (не
  // показываем «—», чтобы не ломать UX при сетевой ошибке).
  let dynamicPrices = {}; // slug -> formatted price string

  function formatPrice(value) {
    if (!Number.isFinite(value)) return '';
    return new Intl.NumberFormat('ru-RU').format(value) + ' ₽';
  }

  onMount(() => {
    now = Date.now();
    intervalId = setInterval(() => {
      now = Date.now();
    }, 1000);

    // Параллельный fetch цен для всех тарифов. Каждый план независим —
    // если один упал, остальные обновятся.
    for (const plan of pricingSection.plans) {
      const id = resolveProductId(plan.slug);
      precalculateOrder(id, null, 1)
        .then((data) => {
          const price = Number(data?.final_price);
          if (Number.isFinite(price)) {
            dynamicPrices = { ...dynamicPrices, [plan.slug]: formatPrice(price) };
          }
        })
        .catch(() => {});
    }
  });

  onDestroy(() => {
    if (intervalId) clearInterval(intervalId);
  });

  $: diff = Math.max(0, targetMs - now);
  $: eventStarted = targetMs - now <= 0;
  $: days = Math.floor(diff / 86_400_000);
  $: hours = Math.floor((diff % 86_400_000) / 3_600_000);
  $: minutes = Math.floor((diff % 3_600_000) / 60_000);
  $: seconds = Math.floor((diff % 60_000) / 1000);
  $: countdownValue = [days, hours, minutes, seconds]
    .map((n) => String(n).padStart(2, '0'))
    .join(' : ');
</script>

<section id="participate" class="pricing-section" aria-labelledby="pricing-title">
  <span id="tariff" class="pricing-anchor" aria-hidden="true"></span>

  <div class="pricing-inner">
    <div class="pricing-content">
      <div class="pricing-head">
        <div class="pricing-copy">
          <div class="heading-group">
            <p class="pricing-kicker">{pricingSection.kicker}</p>
            <h2 id="pricing-title">
              {#each pricingSection.title as line}
                <span>{line}</span>
              {/each}
            </h2>
          </div>

          <p class="pricing-description">
            {#each pricingSection.description as line}
              <span>{line}</span>
            {/each}
          </p>
        </div>

        <div class="countdown" aria-label={eventStarted ? pricingSection.countdown.startedLabel : `${pricingSection.countdown.label} ${countdownValue}`}>
          <span>{eventStarted ? pricingSection.countdown.startedLabel : pricingSection.countdown.label}</span>
          {#if !eventStarted}
            <strong>{countdownValue}</strong>
          {/if}
        </div>
      </div>

      <div class="plans-grid" aria-label="Тарифы участия">
        {#each pricingSection.plans as plan}
          <PricingCard
            {...plan}
            price={dynamicPrices[plan.slug] ?? plan.price}
            action={{ label: pricingSection.actionLabel, href: `/registration?product=${plan.slug}` }}
          />
        {/each}
      </div>

      <div class="pricing-help">
        <div class="help-copy">
          <span class="help-icon" aria-hidden="true">i</span>
          <p>{pricingSection.help.text}</p>
        </div>

        <Button
          href={pricingSection.help.action.href}
          label={pricingSection.help.action.label}
          target={pricingSection.help.action.target}
          rel={pricingSection.help.action.rel}
          variant="primary"
        />
      </div>
    </div>
  </div>
</section>

<style>
  .pricing-section {
    position: relative;
    display: flex;
    justify-content: center;
    padding: 100px 0;
    background: #000000;
    color: #ffffff;
    font-family: Roboto, Arial, Helvetica, sans-serif;
    font-variation-settings: 'wdth' 100;
  }

  .pricing-anchor {
    position: absolute;
    top: 0;
  }

  .pricing-inner {
    display: flex;
    width: min(100%, 1440px);
    align-items: center;
    justify-content: center;
    padding: 0 64px;
    box-sizing: border-box;
  }

  .pricing-content {
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    gap: 48px;
    padding: 0 12px;
    box-sizing: border-box;
  }

  .pricing-head {
    display: flex;
    width: 100%;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
  }

  .pricing-copy {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    flex-direction: column;
    gap: 48px;
  }

  .heading-group {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .pricing-kicker,
  h2,
  .pricing-description {
    margin: 0;
  }

  .pricing-kicker {
    color: #ffa800;
    font-size: 20px;
    font-weight: 700;
    line-height: 20px;
    text-transform: uppercase;
  }

  h2 {
    max-width: 650px;
    color: #ffffff;
    font-size: 56px;
    font-weight: 700;
    line-height: 56px;
  }

  h2 span,
  .pricing-description span {
    display: block;
  }

  .pricing-description {
    max-width: 640px;
    color: rgba(255, 255, 255, 0.5);
    font-size: 24px;
    font-weight: 400;
    line-height: 36px;
  }

  .countdown {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 24px;
    border: 1px solid #ffa800;
    border-radius: 16px;
    font-family: Inter, Roboto, Arial, Helvetica, sans-serif;
    white-space: nowrap;
  }

  .countdown span {
    color: rgba(255, 255, 255, 0.5);
    font-size: 16px;
    font-weight: 400;
    line-height: 1.2;
  }

  .countdown strong {
    color: #ffffff;
    font-size: 40px;
    font-weight: 400;
    line-height: 1.2;
  }

  .plans-grid {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 24px;
    padding: 24px 0;
  }

  /* На узких десктопах (1280–1439) 4 карточки в ряд оставляют ~264px на
     карточку — CTA «Выбрать тариф» с nowrap не помещается, горизонтальное
     переполнение. Используем 2×2 до 1440px. */
  @media (max-width: 1439px) {
    .plans-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 32px;
    }
  }

  .pricing-help {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 24px 48px;
    border: 1px solid #ffa800;
    border-radius: 16px;
    box-sizing: border-box;
  }

  .help-copy {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
  }

  .help-icon {
    display: inline-flex;
    flex: 0 0 auto;
    width: 52px;
    height: 52px;
    align-items: center;
    justify-content: center;
    border: 2px solid #e7b962;
    border-radius: 50%;
    color: #e7b962;
    font-size: 28px;
    font-weight: 400;
    font-style: italic;
    line-height: 1;
  }

  .help-copy p {
    max-width: 456px;
    margin: 0;
    color: #ffffff;
    font-size: 18px;
    font-weight: 400;
    line-height: 24px;
  }

  @media (max-width: 1279px) {
    .pricing-inner {
      padding: 0 32px;
    }

    .pricing-content {
      padding: 0 12px;
    }

    .pricing-head {
      flex-direction: column;
      align-items: center;
      gap: 48px;
    }

    .pricing-copy,
    .pricing-description {
      width: 100%;
    }

    h2 {
      font-size: 40px;
      line-height: 1.1;
    }

    .pricing-description {
      font-size: 18px;
      line-height: 28px;
    }

    .countdown {
      flex-direction: row;
    }

    .countdown strong {
      font-size: 32px;
    }

    /* .plans-grid → 2×2 уже в @media (max-width: 1439px) выше. */

    .pricing-help {
      padding: 24px;
    }

    .help-copy p {
      font-size: 16px;
    }
  }

  @media (max-width: 1599px) and (min-width: 1280px) {
    h2 {
      font-size: 48px;
      line-height: 1.1;
    }

    .pricing-description {
      font-size: 20px;
      line-height: 30px;
    }

    .countdown strong {
      font-size: 36px;
    }
  }

  @media (max-width: 767px) {
    .pricing-section {
      padding: 40px 0;
    }

    .pricing-inner {
      padding: 0 24px;
    }

    .pricing-content {
      gap: 24px;
      padding: 0;
    }

    .pricing-head {
      gap: 48px;
    }

    .pricing-copy {
      gap: 48px;
    }

    h2 {
      font-size: 32px;
      line-height: 32px;
    }

    .pricing-description {
      font-size: 18px;
      line-height: normal;
    }

    .countdown {
      width: 100%;
      flex-direction: column;
      box-sizing: border-box;
    }

    .countdown strong {
      font-size: 36px;
    }

    .plans-grid {
      grid-template-columns: 1fr;
      gap: 48px;
    }

    .pricing-help {
      flex-direction: column;
      align-items: stretch;
      padding: 24px;
    }

    .help-copy p {
      font-size: 16px;
    }

    .pricing-help :global(.button) {
      width: 100%;
    }
  }
</style>
